import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Platform} from "@angular/cdk/platform";
import {lastValueFrom} from "rxjs";
import {Session} from '../domain/session';
import {LoginResponse} from '../domain/login-response';

@Injectable()
export class RequestExecutor {
  constructor(
    private http: HttpClient,
    private session: Session,
    private platform: Platform
  ) {
  }

  async getFile(path: string, params: any = null): Promise<any> {
    try {
      return await lastValueFrom(this.http.get(environment.serverBaseUrl + path, await this.buildFileOptions(params)));
    } catch (error: any) {
      if (error.status == 401 && this.session.getRefreshToken() != null) {
        await this.refreshAccessToken();

        return await lastValueFrom(this.http.get(environment.serverBaseUrl + path, await this.buildFileOptions(params)));
      }

      throw error;
    }
  }

  async get(path: string, params: any = null, responseType: string = null): Promise<any> {
    try {
      return await lastValueFrom(this.http.get(environment.serverBaseUrl + path, await this.buildJsonOptions('', params, responseType)));
    } catch (error: any) {
      if (error.status == 401 && this.session.getRefreshToken() != null) {
        await this.refreshAccessToken();

        return await lastValueFrom(this.http.get(environment.serverBaseUrl + path, await this.buildJsonOptions('', params)));
      }

      throw error;
    }
  }

  async post(path: string, content: any, params: any = null): Promise<any> {
    try {
      return await lastValueFrom(this.http.post(environment.serverBaseUrl + path, JSON.stringify(content), await this.buildJsonOptions(content, params)));
    } catch (error: any) {
      if (error.status == 401 && this.session.getRefreshToken() != null) {
        await this.refreshAccessToken();

        return await lastValueFrom(this.http.post(environment.serverBaseUrl + path, JSON.stringify(content), await this.buildJsonOptions(content, params)));
      }

      throw error;
    }
  }

  async delete(path: string, params: any = null): Promise<any> {
    try {
      return await lastValueFrom(this.http.delete(environment.serverBaseUrl + path, await this.buildJsonOptions('', params)));
    } catch (error: any) {
      if (error.status == 401 && this.session.getRefreshToken() != null) {
        await this.refreshAccessToken();

        return await lastValueFrom(this.http.delete(environment.serverBaseUrl + path, await this.buildJsonOptions('', params)));
      }

      throw error;
    }
  }

  async buildJsonOptions(content: any, params: any = null, responseType: any = null) {
    const headers = await this.buildRequiredHeaders();

    return {
      body: JSON.stringify(content),
      params: this.buildParameters(params),
      headers: headers,
      responseType: responseType != null ? responseType : 'json'
    };
  }

  async buildFileOptions(params: any = null): Promise<Object> {
    const headers = await this.buildRequiredHeaders();

    return {
      params: this.buildParameters(params),
      headers: headers,
      observe: 'response',
      responseType: 'blob'
    };
  }

  private buildParameters(params: any): HttpParams {
    let httpParams = new HttpParams();

    if (params !== null) {
      Object.keys(params).forEach(key => {
        if (params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }

    return httpParams;
  }

  private async buildRequiredHeaders(): Promise<HttpHeaders> {
    let headers = this.buildDefaultHeaders();

    if (this.session.getAccessToken() !== null) {
      headers = headers.set('Authorization', 'Bearer ' + this.session.getAccessToken());
    }

    return headers;
  }

  private buildDefaultHeaders(): HttpHeaders {
    let headers = new HttpHeaders();

    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('X-User-Agent', `TaskrWeb ${environment.version} (${this.getPlatform()})`);
    headers = headers.set('X-Resolution', `${window.innerWidth}x${window.innerHeight}`);

    return headers;
  }

  async refreshAccessToken() {
    const response = <LoginResponse>await lastValueFrom(this.http.get(`${environment.serverBaseUrl}/session/refresh?token=${this.session.getRefreshToken()}`, {headers: this.buildDefaultHeaders()}));

    this.session.setTokens(response.AccessToken, response.RefreshToken);
  }

  getPlatform() {
    if (this.platform.ANDROID) {
      return 'Android';
    }

    if (this.platform.IOS) {
      return 'iOS';
    }

    if (this.platform.FIREFOX) {
      return 'FireFox';
    }

    if (this.platform.BLINK) {
      return 'Blink';
    }

    if (this.platform.WEBKIT) {
      return 'Webkit';
    }

    if (this.platform.TRIDENT) {
      return 'Trident';
    }

    if (this.platform.EDGE) {
      return 'Edge';
    }

    if (this.platform.SAFARI) {
      return 'Safari';
    }

    return 'Unknown';
  }
}

